import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchCryptoPrices, fetchEurUsdRate, fetchStockPrices } from '../services/priceService';
import { buildAsset } from '../services/signalEngine';
// Supabase removed — all data stored locally on device

const REFRESH_INTERVAL_MS = 30_000;

// Ticker → CoinGecko ID mapping
const CRYPTO_ID_MAP = {
  BTC:   'bitcoin',
  ETH:   'ethereum',
  SOL:   'solana',
  ADA:   'cardano',
  XRP:   'ripple',
  DOGE:  'dogecoin',
  BNB:   'binancecoin',
  MATIC: 'matic-network',
  DOT:   'polkadot',
  LINK:  'chainlink',
  AVAX:  'avalanche-2',
  UNI:   'uniswap',
  LTC:   'litecoin',
  ATOM:  'cosmos',
};

const DEFAULT_WATCHLIST = [
  // ── Mega-Cap Tech ──
  { ticker: 'AAPL',  name: 'Apple Inc.',          type: 'stock' },
  { ticker: 'MSFT',  name: 'Microsoft Corp.',      type: 'stock' },
  { ticker: 'NVDA',  name: 'NVIDIA Corp.',         type: 'stock' },
  { ticker: 'GOOGL', name: 'Alphabet Inc.',        type: 'stock' },
  { ticker: 'META',  name: 'Meta Platforms',       type: 'stock' },
  { ticker: 'AMZN',  name: 'Amazon.com Inc.',      type: 'stock' },
  { ticker: 'TSLA',  name: 'Tesla, Inc.',          type: 'stock' },
  // ── Halbleiter ──
  { ticker: 'AMD',   name: 'Advanced Micro Devices', type: 'stock' },
  { ticker: 'INTC',  name: 'Intel Corp.',          type: 'stock' },
  { ticker: 'QCOM',  name: 'Qualcomm Inc.',        type: 'stock' },
  { ticker: 'AVGO',  name: 'Broadcom Inc.',        type: 'stock' },
  // ── Finance ──
  { ticker: 'JPM',   name: 'JPMorgan Chase',       type: 'stock' },
  { ticker: 'GS',    name: 'Goldman Sachs',        type: 'stock' },
  { ticker: 'V',     name: 'Visa Inc.',            type: 'stock' },
  { ticker: 'MA',    name: 'Mastercard',           type: 'stock' },
  { ticker: 'PYPL',  name: 'PayPal Holdings',      type: 'stock' },
  { ticker: 'COIN',  name: 'Coinbase Global',      type: 'stock' },
  // ── Streaming & Entertainment ──
  { ticker: 'NFLX',  name: 'Netflix Inc.',         type: 'stock' },
  { ticker: 'DIS',   name: 'Walt Disney Co.',      type: 'stock' },
  { ticker: 'SPOT',  name: 'Spotify Technology',   type: 'stock' },
  // ── Software & Cloud ──
  { ticker: 'CRM',   name: 'Salesforce Inc.',      type: 'stock' },
  { ticker: 'ORCL',  name: 'Oracle Corp.',         type: 'stock' },
  { ticker: 'ADBE',  name: 'Adobe Inc.',           type: 'stock' },
  { ticker: 'NOW',   name: 'ServiceNow Inc.',      type: 'stock' },
  // ── Mobility & Infra ──
  { ticker: 'UBER',  name: 'Uber Technologies',    type: 'stock' },
  { ticker: 'ABNB',  name: 'Airbnb Inc.',          type: 'stock' },
  // ── Pharma & Gesundheit ──
  { ticker: 'JNJ',   name: 'Johnson & Johnson',    type: 'stock' },
  { ticker: 'PFE',   name: 'Pfizer Inc.',          type: 'stock' },
  { ticker: 'MRK',   name: 'Merck & Co.',          type: 'stock' },
  // ── ETFs ──
  { ticker: 'SPY',   name: 'S&P 500 ETF',          type: 'stock' },
  { ticker: 'QQQ',   name: 'Nasdaq-100 ETF',       type: 'stock' },
  // ── Krypto ──
  { ticker: 'BTC',   name: 'Bitcoin',              type: 'crypto' },
  { ticker: 'ETH',   name: 'Ethereum',             type: 'crypto' },
  { ticker: 'SOL',   name: 'Solana',               type: 'crypto' },
  { ticker: 'XRP',   name: 'Ripple',               type: 'crypto' },
  { ticker: 'BNB',   name: 'BNB',                  type: 'crypto' },
  { ticker: 'DOGE',  name: 'Dogecoin',             type: 'crypto' },
  { ticker: 'AVAX',  name: 'Avalanche',            type: 'crypto' },
  { ticker: 'LINK',  name: 'Chainlink',            type: 'crypto' },
  { ticker: 'ADA',   name: 'Cardano',              type: 'crypto' },
  { ticker: 'DOT',   name: 'Polkadot',             type: 'crypto' },
];

const STORAGE_KEY = 'finanzapp_watchlist_v2';
const PRICES_CACHE_KEY = 'finanzapp_prices_cache_v1';

function loadWatchlist() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return DEFAULT_WATCHLIST;
}

function saveWatchlist(list) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {}
}

function loadCachedPrices() {
  try {
    const raw = localStorage.getItem(PRICES_CACHE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return [];
}

function saveCachedPrices(assets) {
  try {
    localStorage.setItem(PRICES_CACHE_KEY, JSON.stringify(assets));
  } catch {}
}

export function useLivePrices() {
  const [watchlist, setWatchlist] = useState(loadWatchlist);
  const [assets, setAssets] = useState(loadCachedPrices);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const timerRef = useRef(null);

  const loadPrices = useCallback(async () => {
    try {
      const stocks = watchlist.filter((w) => w.type === 'stock').map((w) => w.ticker);
      const cryptos = watchlist.filter((w) => w.type === 'crypto');
      const cryptoIds = cryptos.map((w) => CRYPTO_ID_MAP[w.ticker]).filter(Boolean);

      const [stockData, cryptoData, eurUsd] = await Promise.all([
        stocks.length ? fetchStockPrices(stocks) : Promise.resolve({}),
        cryptoIds.length ? fetchCryptoPrices(cryptoIds) : Promise.resolve({}),
        fetchEurUsdRate()
      ]);

      // CoinGecko gibt Tickers nach symbol zurück, re-mappen
      const cryptoByTicker = {};
      cryptos.forEach((w) => {
        const id = CRYPTO_ID_MAP[w.ticker];
        if (id && cryptoData[w.ticker]) {
          cryptoByTicker[w.ticker] = cryptoData[w.ticker];
        }
      });

      const built = watchlist.map((meta) => {
        const priceData =
          meta.type === 'stock' ? stockData[meta.ticker] : cryptoData[meta.ticker];
        if (!priceData) {
          // Fallback-Asset wenn API nichts geliefert hat
          return {
            ticker: meta.ticker,
            name: meta.name,
            price: 0,
            change: 0,
            change7d: 0,
            signal: 'Halten',
            confidence: 0,
            risk: 'Mittel',
            reasons: ['Keine Live-Daten verfügbar'],
            nextAction: 'Backend prüfen',
            liveData: false
          };
        }
        return buildAsset(meta, priceData, eurUsd);
      });

      setAssets(built);
      saveCachedPrices(built);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      console.error('[useLivePrices]', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [watchlist]);

  useEffect(() => {
    // Don't show loading spinner if we have cached prices already
    const cached = loadCachedPrices();
    if (!cached.length) setLoading(true);
    loadPrices();
    timerRef.current = setInterval(() => {
      loadPrices();
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(timerRef.current);
  }, [loadPrices]); // re-triggert bewusst bei Watchlist-Änderungen

  // Live-tick: random staggered micro fluctuations every 2s per ticker
  const tickRef = useRef(null);
  useEffect(() => {
    tickRef.current = setInterval(() => {
      setAssets(prev => prev.map(a => {
        if (!a.price || a.price <= 0) return a;
        if (Math.random() < 0.45) return a; // ~55% chance to tick each cycle
        // Jitter scaled to last visible digit, not % of price
        const unit = a.price < 1 ? 0.0002 : a.price < 10 ? 0.01 : a.price < 100 ? 0.04 : a.price < 1000 ? 0.15 : 1.5;
        const jitter = (Math.random() - 0.5) * 2 * unit;
        return { ...a, price: parseFloat((a.price + jitter).toFixed(a.price < 1 ? 4 : 2)) };
      }));
    }, 2000);
    return () => clearInterval(tickRef.current);
  }, []);

  const addTicker = useCallback((ticker, name, type) => {
    const upper = ticker.toUpperCase().trim();
    setWatchlist((prev) => {
      if (prev.some((w) => w.ticker === upper)) return prev;
      const next = [...prev, { ticker: upper, name: name || upper, type }];
      saveWatchlist(next);
      return next;
    });
  }, []);

  const removeTicker = useCallback((ticker) => {
    setWatchlist((prev) => {
      const next = prev.filter((w) => w.ticker !== ticker);
      saveWatchlist(next);
      return next;
    });
  }, []);

  const refresh = useCallback(() => {
    setLoading(true);
    loadPrices();
  }, [loadPrices]);

  return { assets, watchlist, loading, error, lastUpdated, addTicker, removeTicker, refresh };
}
